import dayjs from "dayjs";
import { AxiosError, AxiosResponse } from "axios";
import {
  CustomerGroupExportParams,
  ExportChain,
} from "../../types/sales-report-export.type";
import {
  CustomerGroupExportFormat,
  CustomerGroupWithExportSettings,
} from "../../types/customer-group-export.type";
import {
  exportSevenElevenReport,
  exportTescoReport,
  exportTwkReport,
} from "../../services";

/**
 * Which endpoint renders each file layout. The chains themselves come from the API
 * (customer groups with an `export_format`); a new chain on one of these layouts needs
 * no code, a new layout needs a new endpoint and an entry here.
 */
export const CUSTOMER_GROUP_EXPORTERS: Record<
  CustomerGroupExportFormat,
  (params: CustomerGroupExportParams) => Promise<AxiosResponse<Blob>>
> = {
  tesco: exportTescoReport,
  seven_eleven: exportSevenElevenReport,
  twk: exportTwkReport,
};

const isExportChain = (
  group: CustomerGroupWithExportSettings,
): group is CustomerGroupWithExportSettings & {
  export_format: CustomerGroupExportFormat;
} => group.export_format !== null;

/** The customer groups that have an export layout, as the chain cards show them. */
export const toExportChains = (
  groups: CustomerGroupWithExportSettings[],
): ExportChain[] =>
  groups.filter(isExportChain).map((group) => ({
    uuid: group.uuid,
    name: group.name,
    export_format: group.export_format,
    export_file_prefix: group.export_file_prefix,
  }));

/** Used only when a chain has no file prefix set; the server names the file itself. */
const FALLBACK_FILENAME_PREFIX = "Export";

/** How a day is written in the fallback filename, as the server writes it in its own. */
const FILENAME_DATE_FORMAT = "DD-MM-YY";

/** The ทั้งหมด option of the สาขา and สายส่ง pickers; the export calls leave it out. */
export const ALL_OPTION = "all";

/** The widest range one export may cover, both ends counted. */
export const MAX_EXPORT_RANGE_DAYS = 31;

export const getDefaultExportDate = () => dayjs();

export const getExportDateParam = (date: dayjs.Dayjs) => date.format("YYYY-MM-DD");

/** Counted in calendar days with both ends in, so 1–31 October is exactly the limit. */
export const isExportRangeTooLong = (dateFrom: dayjs.Dayjs, dateTo: dayjs.Dayjs) =>
  dateTo.startOf("day").diff(dateFrom.startOf("day"), "day") + 1 > MAX_EXPORT_RANGE_DAYS;

export const getFilenameFromContentDisposition = (header?: string) => {
  if (!header) return "";

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const filenameMatch = header.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] ?? "";
};

/**
 * Fallback download filename, used only when `Content-Disposition` is missing or
 * unparseable. Named the way the server names the file — `<export_file_prefix>_DD-MM-YY`
 * for one day, `<export_file_prefix>_DD-MM-YY_DD-MM-YY` for a range — and from the
 * exported dates rather than "today", so a back-dated export is not saved under the
 * wrong day.
 */
export const getCustomerGroupExportFilename = (
  chain: ExportChain,
  dateFrom: dayjs.Dayjs,
  dateTo: dayjs.Dayjs,
) => {
  const prefix = chain.export_file_prefix ?? FALLBACK_FILENAME_PREFIX;
  const firstDay = dateFrom.format(FILENAME_DATE_FORMAT);
  const days = dateFrom.isSame(dateTo, "day")
    ? firstDay
    : `${firstDay}_${dateTo.format(FILENAME_DATE_FORMAT)}`;
  return `${prefix}_${days}.xlsx`;
};

export const downloadBlob = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getExportErrorMessage = async (error: unknown) => {
  const responseData = (error as AxiosError<Blob>).response?.data;
  if (!(responseData instanceof Blob)) return "";

  const text = await responseData.text();
  if (!text) return "";

  try {
    const data = JSON.parse(text) as {
      message?: string;
      error?: string;
      detail?: string;
    };

    return data.message || data.error || data.detail || "";
  } catch {
    return text;
  }
};
